import { DefaultCoverage } from '@mock/CoverageCategories';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

export interface Subcategory {
  id?: number;
  active?: boolean;
  title: string;
  description?: string;
  icon?: string;
  available?: boolean;
  video_url?: string;
  chosenCoverageIndex?: number;
  coverageAmount?: number;
  monthlyPrice?: number;
}
export interface SubcategoryPrice {
  id: number;
  price: number;
  pricingPlanId: number;
}
export interface Category {
  id?: number;
  active?: boolean;
  title: string;
  subtitle: string;
  code: string;
  description?: string;
  icon?: string;
  video_url?: string;
  available?: boolean;
  subcategories: Subcategory[];
}
export interface CoverageState {
  firstName: string;
  lastName: string;
  email?: string;
  address: string;
  county: string;
  birthDate: string;
  countyFips?: number;
  state: string;
  city: string;
  zip?: string;
  utility: Array<string>;
  pricePerMonth: number;
  categories: Category[];
  preparedUserToken: string;
  subcategoriesActualPrices: SubcategoryPrice[];
  isYearlyChosen: boolean;
}

const initialState: CoverageState = {
  firstName: '',
  lastName: '',
  email: '',
  county: '',
  address: '',
  birthDate: '',
  countyFips: -1,
  state: '',
  city: '',
  categories: [],
  utility: [],
  pricePerMonth: -1,
  preparedUserToken: '',
  subcategoriesActualPrices: [],
  isYearlyChosen: false,
  zip: '',
};
export const CoverageAdapter = createEntityAdapter<CoverageState>();
const updateSubcategoryCoveragePlan = (
  subcategoryAmountPlan: number,
  subcategoryMonthlyPrice: number,
  chosenSubcategoryTitle: string,
  chosenCategoryTitle: string,
  chosenCoverageIndex: number,
  state: CoverageState,
) => {
  const categoryIndex = state.categories.findIndex(
    (item) => item.title === chosenCategoryTitle,
  );
  const subcategoryIndex = state.categories[
    categoryIndex
  ].subcategories.findIndex((item) => item.title === chosenSubcategoryTitle);

  const categories = JSON.parse(JSON.stringify(state.categories));
  categories[categoryIndex].subcategories[subcategoryIndex].monthlyPrice =
    subcategoryMonthlyPrice;
  categories[categoryIndex].subcategories[
    subcategoryIndex
  ].chosenCoverageIndex = chosenCoverageIndex;
  categories[categoryIndex].subcategories[subcategoryIndex].coverageAmount =
    subcategoryAmountPlan;

  return categories;
};

const removeSubcategoryFromActive = (
  categoryTitle: string,
  subcategoryTitle: string,
  state: CoverageState,
) => {
  const categoryIndex = state.categories.findIndex(
    (item) => item.title === categoryTitle,
  );
  const subcategoryIndex = state.categories[
    categoryIndex
  ].subcategories.findIndex((item) => item.title === subcategoryTitle);
  const categories = JSON.parse(JSON.stringify(state.categories));
  categories[categoryIndex].subcategories.splice(subcategoryIndex, 1);

  if (categories[categoryIndex].subcategories.length === 0) {
    categories.splice(categoryIndex, 1);
  }

  return categories;
};
const coverageSlice = createSlice({
  name: 'coverage',
  initialState: CoverageAdapter.getInitialState(initialState),
  reducers: {
    initCategories: (state, action) => {
      return {
        ...state,
        categories: action.payload,
      };
    },
    removeSubcategory: (state, action) => {
      const { categoryTitle, subcategoryTitle } = action.payload;
      const result = removeSubcategoryFromActive(
        categoryTitle,
        subcategoryTitle,
        state,
      );

      return {
        ...state,
        categories: result,
      };
    },
    setActiveCategory: (state, action) => {
      const { categoryTitle, fullCategoriesList } = action.payload;

      const categories = JSON.parse(JSON.stringify(fullCategoriesList));
      const category = categories.find(
        (category: Category) => categoryTitle === category.title,
      );
      category.subcategories = [];
      if (state.categories.find((category) => category.title === categoryTitle))
        return { ...state };

      return { ...state, categories: [...state.categories, category] };
    },
    setSubcategoriesActive: (state, action) => {
      const { category, selectedSubcategoryTitleList } = action.payload;

      const categories = JSON.parse(JSON.stringify(state.categories));
      const categoryCopy = JSON.parse(JSON.stringify(category));

      const subcategories = categoryCopy.subcategories.filter(
        (subcategory: Subcategory) => {
          return selectedSubcategoryTitleList.includes(subcategory.title);
        },
      );
      subcategories.map((subcategory: Subcategory) => {
        subcategory.monthlyPrice = DefaultCoverage.MonthlyPrice;
        subcategory.coverageAmount = DefaultCoverage.CoverageAmount;
        subcategory.chosenCoverageIndex = DefaultCoverage.ChosenCoverageIndex;
        return subcategory;
      });

      const index = categories.findIndex(
        (category: Category) => category.id === categoryCopy.id,
      );
      const concatSubcategories = [
        ...categories[index].subcategories,
        ...subcategories,
      ];
      categoryCopy.subcategories = concatSubcategories;
      categories[index] = categoryCopy;
      return { ...state, categories: categories };
    },
    updateActivePlan: (state, action) => {
      const result = updateSubcategoryCoveragePlan(
        action.payload.subcategoryAmountPlan,
        action.payload.subcategoryMonthlyPrice,
        action.payload.chosenSubcategoryTitle,
        action.payload.chosenCategoryTitle,
        action.payload.chosenCoverageIndex,
        state,
      );
      return {
        ...state,
        categories: result,
      };
    },
    updateUserIdentityInfo: (state, action) => {
      return {
        ...state,
        state: action.payload.state,
        county: action.payload.county,
        countyFips: action.payload.countyFips,
        city: action.payload.city,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        address: action.payload.address,
        preparedUserToken: action.payload.token,
        zip: action.payload.zip,
      };
    },
    updatePreparedUserToken: (state, action) => {
      return {
        ...state,
        preparedUserToken: action.payload,
      };
    },
    updateSubcategoryActualPrices: (state, action) => {
      return {
        ...state,
        subcategoriesActualPrices: action.payload,
      };
    },
    setIsYearlyChosen: (state, action) => {
      return {
        ...state,
        isYearlyChosen: action.payload,
      };
    },
  },
});
export default coverageSlice.reducer;
export const {
  updateActivePlan,
  initCategories,
  removeSubcategory,
  setSubcategoriesActive,
  updateUserIdentityInfo,
  updatePreparedUserToken,
  setActiveCategory,
  updateSubcategoryActualPrices,
  setIsYearlyChosen,
} = coverageSlice.actions;
